// Pedir y esperar los prompts del catálogo (ADR-2026-10-14-el-escritor-de-catalogo-se-polea):
// `POST /v1/catalogo/escribir-prompts` encola un job por producto y responde los `execution_id`;
// acá se polea `GET …?ids=` cada 2 s y cada producto que termina se vuelca al plan al instante.
// Las funciones puras (armar el cuerpo, volcar la salida) van primero, con tests.
import { atlasBackendFetch, atlasBackendGet } from '../../../../lib/atlasBackend';
import { proporcionLegible, resumenMedidas, tieneMedidasCompletas } from '../../../../lib/medidas';
import { TAREAS_DE_IMAGEN, aspectDelFormato, datosDelProducto, pillsDeLaFicha, type ColorDeInfografia, type ItemDelPlan, type PlanDeCatalogo, type TareaDeImagen } from './plan';

export interface TomaParaElEscritor {
  id: string;
  titulo: string;
  que_muestra: string;
  fondo_blanco?: boolean;
  luz_fondo_blanco?: 'difusa' | 'dura';
  tarea?: TareaDeImagen;
  /** Las imágenes de la persona que viajan con la toma: el escritor las MIRA y las nombra por
   *  su rótulo (ADR-2026-10-15-la-plantilla-de-marca). */
  referencias?: Array<{ etiqueta: string; storage_path: string; bucket?: string }>;
  /** La ficha de medidas: el backend llena la plantilla de infografía con esto
   *  (ADR-2026-10-14-la-infografia-es-un-prompt-mas). */
  infografia?: { pills: string[]; color: ColorDeInfografia; estilo_grafico?: string; referencias?: string[] };
}

export interface ProductoParaElEscritor {
  product_id: string;
  nombre: string;
  notas?: string;
  datos: Array<{ clave: string; valor: string }>;
  /** Las cotas legibles y la proporción, para la infografía (sólo con medidas completas). */
  medidas?: { resumen: string; proporcion: string | null };
  tomas: TomaParaElEscritor[];
}

export interface CuerpoDelEscritor {
  estilo: string;
  /** Proporción ("4:5"), como la recibe el backend. */
  formato: string;
  /** Lo que la persona pidió cambiar en ESTA reescritura, para todos los productos del pedido
   *  (ADR-2026-10-14-el-agente-edita-el-catalogo-en-masa). */
  indicacion?: string;
  productos: ProductoParaElEscritor[];
}

export const MAX_INDICACION = 600;

/** Los ítems a escribir → un producto por cada uno que tenga alguno, con sólo las tomas pedidas. */
export function cuerpoParaElEscritor(plan: PlanDeCatalogo, items: ItemDelPlan[], indicacion?: string): CuerpoDelEscritor {
  const pedidos = new Set(items.map((i) => i.id));
  const tomas = new Map(plan.tomas.map((t) => [t.id, t]));
  // Las etiquetas con que viajan las referencias de una toma: la plantilla las nombra.
  const referenciasDeLaToma = (t: PlanDeCatalogo['tomas'][number]): string[] =>
    (t.referencias ?? []).map((id) => plan.referencias.find((r) => r.id === id)?.etiqueta).filter((x): x is string => !!x);
  const imagenesDeLaToma = (t: PlanDeCatalogo['tomas'][number]): NonNullable<TomaParaElEscritor['referencias']> =>
    (t.referencias ?? []).flatMap((id) => {
      const r = plan.referencias.find((x) => x.id === id);
      return r ? [{ etiqueta: r.etiqueta, storage_path: r.storagePath, ...(r.bucket ? { bucket: r.bucket } : {}) }] : [];
    });
  const productos: ProductoParaElEscritor[] = [];
  for (const p of plan.productos) {
    const suyas: TomaParaElEscritor[] = [];
    for (const it of p.items) {
      if (!pedidos.has(it.id)) continue;
      const t = tomas.get(it.tomaId);
      if (!t) continue;
      suyas.push({
        id: t.id,
        titulo: t.titulo,
        que_muestra: t.queMuestra,
        ...(t.fondoBlanco ? { fondo_blanco: true } : {}),
        ...(t.fondoBlanco && t.luzFondoBlanco ? { luz_fondo_blanco: t.luzFondoBlanco } : {}),
        ...(t.tarea ? { tarea: t.tarea } : {}),
        ...(imagenesDeLaToma(t).length ? { referencias: imagenesDeLaToma(t) } : {}),
        ...(t.infografia
          ? {
              infografia: {
                pills: pillsDeLaFicha(p, t),
                color: t.colorInfografia ?? 'blanco',
                ...(t.estiloGrafico?.trim() ? { estilo_grafico: t.estiloGrafico.trim() } : {}),
                ...(referenciasDeLaToma(t).length ? { referencias: referenciasDeLaToma(t) } : {}),
              },
            }
          : {}),
      });
    }
    if (suyas.length === 0) continue;
    const resumen = tieneMedidasCompletas(p.medidas) ? resumenMedidas(p.medidas) : null;
    productos.push({
      product_id: p.varianteId,
      nombre: p.nombre,
      ...(p.notas?.trim() ? { notas: p.notas.trim() } : {}),
      datos: datosDelProducto(p),
      ...(resumen ? { medidas: { resumen, proporcion: proporcionLegible(p.medidas) } } : {}),
      tomas: suyas,
    });
  }
  const texto = indicacion?.trim().slice(0, MAX_INDICACION);
  return { estilo: plan.estilo, formato: aspectDelFormato(plan.formato), ...(texto ? { indicacion: texto } : {}), productos };
}

/** Un ítem que ya tiene foto no se rompe si su reescritura falla: sigue generado, con su prompt y su
 *  foto, y el motivo a la vista. Sin foto, queda en fallo como siempre. */
const tieneFoto = (it: ItemDelPlan): boolean => it.estado === 'generada' && (it.imageIds?.length ?? 0) > 0;
const conFalloDeEscritura = (it: ItemDelPlan, motivo: string): ItemDelPlan =>
  tieneFoto(it)
    ? { ...it, motivo: `no se pudo reescribir: ${motivo}` }
    : { ...it, prompt: '', estado: 'fallo', motivo };

export interface SalidaDelEscritor {
  items: Array<{ toma_id: string; prompt?: string; tarea?: string; piel_expuesta?: boolean; fallo?: string }>;
}

/** Lo que el escritor devolvió de UN producto → sus ítems (escrito o fallo). Un ítem fijado no se toca. */
export function volcarSalida(plan: PlanDeCatalogo, varianteId: string, salida: SalidaDelEscritor): PlanDeCatalogo {
  const porToma = new Map(salida.items.map((i) => [i.toma_id, i]));
  let cambio = false;
  const productos = plan.productos.map((p) => {
    if (p.varianteId !== varianteId) return p;
    const items = p.items.map((it) => {
      if (it.fijado) return it;
      const s = porToma.get(it.tomaId);
      if (!s) return it;
      cambio = true;
      const prompt = (s.prompt ?? '').trim();
      if (!prompt) return conFalloDeEscritura(it, s.fallo ?? 'el escritor no devolvió esta toma');
      const esTarea = (v: unknown): v is TareaDeImagen => typeof v === 'string' && (TAREAS_DE_IMAGEN as readonly string[]).includes(v);
      return {
        ...it,
        prompt,
        estado: 'escrito' as const,
        motivo: undefined,
        ...(esTarea(s.tarea) ? { tarea: s.tarea } : {}),
        ...(typeof s.piel_expuesta === 'boolean' ? { pielExpuesta: s.piel_expuesta } : {}),
      };
    });
    return { ...p, items };
  });
  return cambio ? { ...plan, productos } : plan;
}

/** Los ítems pedidos de un producto (sin `itemIds`, todos) que no están fijados → fallo con un
 *  motivo (el job falló entero). Los que no se pidieron no se tocan. */
export function fallarProducto(plan: PlanDeCatalogo, varianteId: string, motivo: string, itemIds?: readonly string[]): PlanDeCatalogo {
  return {
    ...plan,
    productos: plan.productos.map((p) =>
      p.varianteId !== varianteId
        ? p
        : {
            ...p,
            items: p.items.map((it) =>
              it.fijado || (itemIds && !itemIds.includes(it.id))
                ? it
                : tieneFoto(it)
                  ? conFalloDeEscritura(it, motivo)
                  : { ...it, estado: 'fallo' as const, motivo },
            ),
          },
    ),
  };
}

const POLL_MS = 2_000;

/**
 * Cuánto se espera al escritor: la cola `categorize` corre de a 2 y cada producto son 5-15 s,
 * así que es proporcional (10 s por producto), entre 3 y 15 minutos (review 2026-10-14).
 */
export const esperaMaximaMs = (productos: number): number => Math.min(900_000, Math.max(180_000, productos * 10_000));
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface ResultadoDeEscritura {
  plan: PlanDeCatalogo;
  escritos: number;
  fallidos: Array<{ producto: string; motivo: string }>;
}

interface EjecucionDelGet {
  execution_id: string;
  status: string;
  output: { product_id?: string; items?: SalidaDelEscritor['items'] } | null;
  error: string | null;
}

/**
 * Manda los ítems al escritor y espera, volcando cada producto en cuanto termina (`onProducto`
 * recibe el plan actualizado). Resuelve cuando terminaron todos o a los 3 minutos (lo que quedó
 * abierto se marca como fallo). Nunca tira: los errores vuelven en `fallidos`.
 */
export async function escribirPrompts(p: {
  plan: PlanDeCatalogo;
  items: ItemDelPlan[];
  /** Una indicación puntual para esta reescritura. */
  indicacion?: string;
  onProducto: (plan: PlanDeCatalogo) => void;
  signal?: AbortSignal;
}): Promise<ResultadoDeEscritura> {
  let plan = p.plan;
  const fallidos: Array<{ producto: string; motivo: string }> = [];
  const nombreDe = (varianteId: string) => plan.productos.find((x) => x.varianteId === varianteId)?.nombre ?? 'un producto';
  const cuerpo = cuerpoParaElEscritor(plan, p.items, p.indicacion);
  const pedidos = p.items.map((i) => i.id);
  const fallar = (pl: PlanDeCatalogo, varianteId: string, motivo: string) => fallarProducto(pl, varianteId, motivo, pedidos);
  if (cuerpo.productos.length === 0) return { plan, escritos: 0, fallidos };

  let encolados: Array<{ product_id: string; execution_id: string }> = [];
  try {
    const res = await atlasBackendFetch('/v1/catalogo/escribir-prompts', cuerpo as unknown as Record<string, unknown>, p.signal ? { signal: p.signal } : undefined);
    const json = (await res.json().catch(() => ({}))) as { productos?: Array<{ product_id: string; execution_id: string }>; error?: string };
    encolados = Array.isArray(json.productos) ? json.productos : [];
    if (!res.ok && res.status !== 429) {
      const motivo = json.error ? `no se pudo pedir la escritura: ${json.error}` : 'no se pudo pedir la escritura';
      for (const prod of cuerpo.productos) {
        plan = fallar(plan, prod.product_id, motivo);
        fallidos.push({ producto: prod.nombre, motivo });
      }
      p.onProducto(plan);
      return { plan, escritos: 0, fallidos };
    }
    const enCola = new Set(encolados.map((e) => e.product_id));
    for (const prod of cuerpo.productos) {
      if (enCola.has(prod.product_id)) continue;
      const motivo = 'la cola está llena, reintentá en un rato';
      plan = fallar(plan, prod.product_id, motivo);
      fallidos.push({ producto: prod.nombre, motivo });
    }
    if (fallidos.length > 0) p.onProducto(plan);
  } catch {
    const motivo = 'no pude conectarme con el escritor';
    for (const prod of cuerpo.productos) {
      plan = fallar(plan, prod.product_id, motivo);
      fallidos.push({ producto: prod.nombre, motivo });
    }
    p.onProducto(plan);
    return { plan, escritos: 0, fallidos };
  }

  const productoDeEjecucion = new Map(encolados.map((e) => [e.execution_id, e.product_id]));
  const abiertas = new Set(productoDeEjecucion.keys());
  let escritos = 0;
  const inicio = Date.now();
  const esperaMaxima = esperaMaximaMs(cuerpo.productos.length);
  while (abiertas.size > 0 && Date.now() - inicio < esperaMaxima && !p.signal?.aborted) {
    await sleep(POLL_MS);
    let ejecuciones: EjecucionDelGet[] = [];
    try {
      const res = await atlasBackendGet(`/v1/catalogo/escribir-prompts?ids=${[...abiertas].join(',')}`);
      if (!res.ok) continue;
      const json = (await res.json()) as { ejecuciones?: EjecucionDelGet[] };
      ejecuciones = Array.isArray(json.ejecuciones) ? json.ejecuciones : [];
    } catch {
      continue;
    }
    let hubo = false;
    for (const e of ejecuciones) {
      if (!abiertas.has(e.execution_id)) continue;
      const varianteId = productoDeEjecucion.get(e.execution_id)!;
      if (e.status === 'succeeded') {
        abiertas.delete(e.execution_id);
        const items = e.output?.items ?? [];
        plan = volcarSalida(plan, varianteId, { items });
        escritos += items.filter((i) => (i.prompt ?? '').trim()).length;
        for (const i of items) if (!(i.prompt ?? '').trim()) fallidos.push({ producto: nombreDe(varianteId), motivo: i.fallo ?? 'toma sin prompt' });
        hubo = true;
      } else if (e.status === 'failed') {
        abiertas.delete(e.execution_id);
        const motivo = e.error ?? 'el escritor falló';
        plan = fallar(plan, varianteId, motivo);
        fallidos.push({ producto: nombreDe(varianteId), motivo });
        hubo = true;
      }
    }
    if (hubo) p.onProducto(plan);
  }
  if (abiertas.size > 0) {
    const motivo = p.signal?.aborted ? 'se canceló la escritura' : 'el escritor tardó demasiado';
    for (const e of abiertas) {
      const varianteId = productoDeEjecucion.get(e)!;
      plan = fallar(plan, varianteId, motivo);
      fallidos.push({ producto: nombreDe(varianteId), motivo });
    }
    p.onProducto(plan);
  }
  return { plan, escritos, fallidos };
}
